<!-- src/pages/MyPage.vue -->
<script setup>
import useAuth from '@/composables/useAuth';
import { jwtDecode } from 'jwt-decode';
import { getProducts, createOrder, ownPoint } from '@/api/PaymentApi';
import { getTermsAndPoliciesAPIOne } from '@/api/Join';
import CustomRadiosPoint from '@/@core/components/app-form-elements/CustomRadiosPoint.vue';
import SmartwelEntryRow from '@/components/partner/SmartwelEntryRow.vue';
import IconHeaderClose from '@/components/svg/IconHeaderClose.vue';
import MypageLnbTabMenu from '@/components/MypageLnbTabMenu.vue';
import { useClientType } from '@/composables/useClientType';
import { useBackLayer } from '@/composables/useAppBack';
import { useRnBridge } from '@/composables/useRnBridge';
import { useRnMessage } from '@/composables/useRnMessage';
import { gnbMenuLinks } from '@/constants/menuLinks';
import { gnbContentTypeMap as contentTypeMap } from '@/constants/logContentType';
import { addContentLogAPI } from '@/api/logServiceApi';
import { canonicalUrl, applySeoMeta } from '@/utils/seo';
import { useStore } from 'vuex';
import { useMyInfoStore } from '@/store/modules/myinfo';
import { clearMySajuState } from '@/composables/calendar/mySaju/useMySaju';
import AlertComponent from '@/components/common/Alert.vue';
import ConfirmComponent from '@/components/common/Confirm.vue';
import DropdownCreditInfo from '@/components/common/DropdownCreditInfo.vue';

definePage({ meta: { layout: 'purchase', title: '서비스구매' } });

const creditInfoRef = ref(null);

const { userInfo } = useAuth();
const store = useStore();
const myInfoStore = useMyInfoStore();
const { sendMessageToApp, debuggingHandler } = useRnBridge();
const {
  dialogTitle,
  dialogTitle2,
  dialogText,
  dialogWarning,
  dialogNextUrl,
  alertRef,
  showAlert,
  confirmRef,
  showConfirm,
} = useDialogManager();

const radioContent = ref(null);
const selectedRadio = ref('');
const goodsAmt = ref(3000);
const goodsName = ref('');
const goodsOrderName = ref('');

const currentPoints = ref(0);
const currentBonusPoint = ref(0);
const currentJustPoint = ref(0);
const currentPointLabel = ref(null);
const currentPcBonusPoint = ref(0);
const agree = ref(false);

// --- 복지찬스(SmartWel) 회원: 잔액 표시 + 전환 페이지(/purchase/exchange) 진입만 유지 ---
// (복지P → 낭만P 전환 인라인 기능은 /purchase/exchange 페이지로 분리됨)
const { isSmartwelMember, partner } = useMember();
const smartwellBalanceKrw = computed(() => partner.value?.totalPoint ?? 0);

// --- IAP 모드 (앱 WebView에서 인앱결제) ---
const isIapMode = ref(false);
const iapProducts = ref(null); // { baseProducts, storeProducts }
const isPurchasing = ref(false);

const router = useRouter();
const route = useRoute();
const { isApp: isWebView, isMobileUi: isMobile } = useClientType();

applySeoMeta({
  title: '포인트 충전, 보너스 최대 45% 추가 적립',
  description:
    '낭만만세력 포인트 충전 시 금액별 보너스와 추가 5% 적립 혜택으로 최대 45% 더 받을 수 있습니다. 600P부터 20,000P까지 선택 가능.',
  url: canonicalUrl(route.fullPath),
});

const openTicketTab = () => {
  router.push('/purchase/tickets');
};

const openGiftTab = () => {
  if (!isLoggedIn.value || (!isWebView.value && !isTokenValid())) {
    showConfirm({
      title: '로그인 후 이용 가능합니다.',
      title2: '로그인 페이지로 이동하시겠습니까?',
      text: '확인',
      onConfirm: () => confirmLogin(),
    });
    return;
  }
  router.push('/purchase/gift-box');
};

const handleClose = () => {
  router.replace('/main');
};

/** 복지찬스 회원: 복지포인트 전환 페이지로 이동 */
const goExchange = () => {
  router.push('/purchase/exchange');
};

// 모바일에서는 gridColumn 비활성화 (Vuetify VCol inline 스타일 방지)
const pointGridColumn = computed(() => {
  if (isMobile.value) return { cols: '12' };
  return { sm: '3', cols: '5' };
});

// --- 마이페이지 드롭다운 메뉴 (모바일 웹 전용) ---
const isMypageMenuOpen = ref(false);

const toggleMypageMenu = () => {
  isMypageMenuOpen.value = !isMypageMenuOpen.value;
};

const closeMypageMenu = () => {
  isMypageMenuOpen.value = false;
};

watch(
  () => route.path,
  () => {
    if (isMypageMenuOpen.value) isMypageMenuOpen.value = false;
  },
);

const isLoggedIn = computed(() => {
  const userId = store.getters['user/id'];
  const userName = store.getters['user/name'];
  try {
    const userInfo = JSON.parse(sessionStorage.getItem('userInfo') || 'null');
    return !!(userId || userName || userInfo?.name || rnAccessToken.value);
  } catch {
    return !!(userId || userName);
  }
});

const displayName = computed(() => {
  if (isLoggedIn.value) {
    return myInfoStore?.displayName || store.getters['user/name'] || '';
  }
  return '';
});
const isHomeRoute = computed(() => route.path === '/main' || route.path === '/');

const handleGoHome = () => {
  closeMypageMenu();
  if (!isHomeRoute.value) {
    router.push('/main');
  }
};

const handleLogin = () => {
  addContentLogAPI('gnb_btn_join');
  router.push({ path: '/auth/login', query: { to: route.fullPath } });
  closeMypageMenu();
};

const isMenuActive = (menuLink) => {
  const currentPath = route.path;
  switch (menuLink.name) {
    case '낭만분석':
      return (
        currentPath.startsWith('/saju-service') ||
        currentPath.startsWith('/saju-result') ||
        currentPath.startsWith('/saju-insight') ||
        currentPath.startsWith('/analysis')
      );
    default:
      return currentPath.startsWith(menuLink.basePath || menuLink.link);
  }
};

const handleMenuClick = (menu) => {
  addContentLogAPI(contentTypeMap[menu.link]);
  if (menu.name !== '낭만만세력은?') router.push(menu.link);
  closeMypageMenu();
};

const handleLogout = async () => {
  addContentLogAPI('pi_btn_logout');
  try {
    await store.dispatch('user/logout');
    clearMySajuState();
    await router.push('/auth/login');
    myInfoStore.resetSelectedOption();
    closeMypageMenu();
  } catch (error) {
    console.error('Logout error:', error);
  }
};

/**
 * 낭만 포인트 충전 > 성공시 URL 생성
 *
 * - next: 경로 (예: '/calendar/fortune')
 * - menu: 상세분석 > selectedMenu (예: /summary)
 */
function buildSuccessUrl(next, menu) {
  const params = new URLSearchParams();

  if (next) params.append('next', next);

  return `${window.location.origin}/purchase/nmspFinishPoint?${params.toString()}`;
}

// [Modified] dynamic generation inside doPayment
// const nextParam = route.query?.next || '/purchase/points';
// const successUrl = buildSuccessUrl(nextParam);

const orderParam = ref(null);
const popTossPayment = ref(null);
const confirmLogin = () => {
  if (isWebView.value) {
    sendMessageToApp({ action: 'login' });
  } else {
    router.push({ path: '/auth/login', query: { to: `${route.path}` } });
  }
};

const isTokenValid = () => {
  const token = sessionStorage.getItem('X-Token');
  if (!token) return false;
  try {
    const { exp } = jwtDecode(token);
    return exp > Date.now() / 1000;
  } catch {
    return false;
  }
};

const openPayment = async () => {
  if (!isLoggedIn.value || (!isIapMode.value && !isWebView.value && !isTokenValid())) {
    showConfirm({
      title: '로그인 후 이용 가능합니다.',
      title2: '로그인 페이지로 이동하시겠습니까?',
      text: '확인',
      onConfirm: () => confirmLogin(),
    });
    return;
  }

  // IAP 모드: 앱으로 구매 요청
  if (isIapMode.value) {
    if (isPurchasing.value) return;
    isPurchasing.value = true;
    sendMessageToApp({ action: 'requestIapPurchase', sku: selectedRadio.value });
    return;
  }

  // Toss PG 모드
  let orderFlag = false;
  orderFlag = await getOrderNo();
  if (!orderFlag) return;

  popTossPayment.value = true;
  await goPayment();
};

const close = () => {
  popTossPayment.value = false;
  // Reinitialize Toss payment module
  if (!isIapMode.value) {
    tossPayments = TossPayments(clientKey);
    widgets = tossPayments.widgets({ customerKey });
  }
};
// ------  결제위젯 초기화 (IAP 모드가 아닐 때만) ------
const clientKey = import.meta.env.VITE_TOSS_CLIENTKEY_NOR;

let tossPayments = null;
let widgets = null;

const customerKey = '3H3NxXvLfVUfZ_DPFyQ_61';

const initTossPayments = () => {
  if (isIapMode.value) return;
  tossPayments = TossPayments(clientKey);
  widgets = tossPayments.widgets({ customerKey });
};

// 초기화는 onMounted 이후에서 실행 (isIapMode 판별 후)
onMounted(() => {
  // 포인트 충전 페이지 진입 로그 (신규 type, DB 등록 후 작동)
  addContentLogAPI('S_point_charge_view');
  nextTick(() => {
    if (!isIapMode.value) initTossPayments();
  });
});

const goPayment = async () => {
  // ------ 주문의 결제 금액 설정 ------
  await widgets.setAmount({
    currency: 'KRW',
    value: orderParam.value.totalAmount,
  });

  await Promise.all([
    // ------  결제 UI 렌더링 ------
    widgets.renderPaymentMethods({
      selector: '#payment-method',
      variantKey: 'DEFAULT',
    }),
    // ------  이용약관 UI 렌더링 ------
    widgets.renderAgreement({ selector: '#agreement', variantKey: 'AGREEMENT' }),
  ]);
};
const doPayment = async () => {
  // [Modified] successUrl 동적 생성
  const currentNext = route.query?.next || '/main';
  const dynamicSuccessUrl = buildSuccessUrl(currentNext);

  await widgets.requestPayment({
    orderId: orderParam.value.orderId,
    orderName: orderParam.value.orderName,
    successUrl: dynamicSuccessUrl,
    failUrl: window.location.origin + '/purchase/nmspFinishPointFail',
    customerEmail: null,
    customerName: userInfo?.name,
    customerMobilePhone: null,
  });
};

//TODO: createOrder
const getOrderNo = async () => {
  const params = {
    amount: goodsAmt.value,
    pointAmount: 0,
    totalAmount: goodsAmt.value,
    productId: selectedRadio.value,
    orderName: goodsName.value,
  };

  let orderData = null;
  try {
    orderData = await createOrder(params);
    orderParam.value = orderData;
    return true;
  } catch (error) {
    console.log('error', error);
    if (error.response?.data?.detail) {
      showAlert({ title: error.response.data.detail });
    } else {
      showAlert({ title: '일시적인 오류가 발생했습니다. 다시 시도해주세요.' });
    }
    return false;
  }
};

const formatNumber = (num) => {
  if (num < 1000) return num;
  return new Intl.NumberFormat().format(num);
};

//TODO: 상품 목록 (웹 전용)
const goodsList = async () => {
  const params = { platform: 'WEB', productType: 'POINT', size: -1, sort: 'price.asc' };
  const data = await getProducts(params);
  if (isIapMode.value) return; // IAP가 선점했으면 덮어쓰지 않음
  radioContent.value = data.content;

  if (isSmartwelMember.value && isMobile.value) {
    selectedRadio.value = '';
    updateGoods();
    return;
  }

  // isDefault 상품 찾기, 없으면 첫 번째 상품
  const defaultItem = radioContent.value.find((item) => item.isDefault);
  selectedRadio.value = defaultItem?.productId || radioContent.value[0]?.productId;
  updateGoods();
};

/** 스토어 가격을 정수로 안전 변환 (Android react-native-iap의 "₩3,300" 형식 대응) */
const parseStorePrice = (sp) => {
  const direct = Number(sp?.price);
  if (Number.isFinite(direct) && direct > 0) return direct;
  const raw = String(sp?.price ?? sp?.localizedPrice ?? '');
  const num = Number(raw.replace(/[^\d.-]/g, ''));
  return Number.isFinite(num) ? num : 0;
};

/** IAP 상품으로 radioContent 세팅 */
const setupIapMode = (products) => {
  if (!products?.baseProducts || !products?.storeProducts) return;
  isIapMode.value = true;
  iapProducts.value = products;

  const storeMap = {};
  products.storeProducts.forEach((sp) => {
    storeMap[sp.productId] = sp;
  });

  radioContent.value = products.baseProducts
    .filter((bp) => storeMap[bp.productId])
    .map((bp) => ({
      ...bp,
      price: parseStorePrice(storeMap[bp.productId]),
      localizedPrice: storeMap[bp.productId].localizedPrice,
    }));

  debuggingHandler({
    action: 'debug',
    message: `[points] radioContent(${radioContent.value.length}건): ${JSON.stringify(radioContent.value.map((r) => ({ productId: r.productId, name: r.name, price: r.price, localizedPrice: r.localizedPrice, bonusPoint: r.detail?.bonusPoint ?? 0 })))}`,
  });

  if (isSmartwelMember.value && isMobile.value) {
    selectedRadio.value = '';
    updateGoods();
  } else {
    const defaultItem = radioContent.value.find((item) => item.isDefault);
    selectedRadio.value = defaultItem?.productId || radioContent.value[0]?.productId;
    updateGoods();
  }

  // shortageAmount 처리
  const shortageAmount = route.query.shortageAmount;
  if (shortageAmount) pickDefaultProduct(shortageAmount);
};

//TODO: 보유 포인트
const getPoint = async () => {
  const params = {};
  const data = await ownPoint(params);
  currentPoints.value = data;
};

/** 보너스 포인트 포함 여부 */
const INCLUDE_BONUS = true;

/** shortageAmount 포맷팅 */
const toPointInt = (pointString) => {
  // 숫자면 바로 처리(부호/소수점 정리)
  if (typeof pointString === 'number' && Number.isFinite(pointString)) {
    return Math.trunc(Math.abs(pointString));
  }

  // 문자열 등 그 외 타입 처리
  const rawText = String(pointString ?? '').trim();
  if (rawText === '') return 0;

  // 숫자 그룹만 추출해서 이어붙임 (예: "2,000P" -> ["2","000"] -> "2000")
  const digitGroups = rawText.match(/\d+/g);
  if (!digitGroups) return 0;

  const concatenatedDigits = digitGroups.join('');
  const parsedValue = Number.parseInt(concatenatedDigits, 10);

  return Number.isNaN(parsedValue) ? 0 : parsedValue;
};

/** 상품에서 '지급 포인트' 추출 (보너스 포함 여부 옵션) */
const normalizePointDetails = (item) => {
  const rawDetails = Array.isArray(item?.detail) ? item.detail : item?.detail ? [item.detail] : [];

  return rawDetails.map((detail) => ({
    point: Number(detail?.point ?? 0),
    bonusPoint: Number(detail?.bonusPoint ?? 0),
    bonusPointLabel: detail?.bonusPointLabel ?? '',
  }));
};

const extractPointSummary = (item) => {
  const details = normalizePointDetails(item);
  const pointDetail = details.find((detail) => detail.point > 0) ?? details[0] ?? null;
  const basePoint = Number(pointDetail?.point ?? 0);

  const bonusDetails = details.filter((detail) => detail.bonusPoint > 0);
  const pcBonusIdx =
    bonusDetails.findIndex((detail) => detail.point === 0) !== -1
      ? bonusDetails.findIndex((detail) => detail.point === 0)
      : bonusDetails.findIndex((detail) => /pc/i.test(detail.bonusPointLabel));
  const pcBonusDetail = pcBonusIdx !== -1 ? bonusDetails[pcBonusIdx] : null;
  const mainBonusDetail = bonusDetails.find((_, idx) => idx !== pcBonusIdx) ?? null;

  const mainBonusPoint = Number(mainBonusDetail?.bonusPoint ?? 0);
  const mainBonusLabel = mainBonusDetail?.bonusPointLabel ?? '';
  const pcBonusPoint = Number(pcBonusDetail?.bonusPoint ?? 0);
  const totalBonusPoint = bonusDetails.reduce((sum, detail) => sum + detail.bonusPoint, 0);

  return { basePoint, mainBonusPoint, mainBonusLabel, pcBonusPoint, totalBonusPoint };
};

const getAwardedPoint = (item, includeBonus = false) => {
  const { basePoint, totalBonusPoint } = extractPointSummary(item);
  return includeBonus ? basePoint + totalBonusPoint : basePoint;
};

/** shortageAmount 에 맞는 포인트 상품 찾기 */
const findRequireRadio = (shortageAmount) => {
  if (!Number.isFinite(shortageAmount) || shortageAmount <= 0) return null;

  const list = radioContent.value ?? [];
  if (!Array.isArray(list) || list.length === 0) return null;

  let candidate = null; // shortageAmount 이상 중 최소
  let candidatePts = Infinity;

  let maxItem = null; // 전체 최대 포인트
  let maxPts = -1;

  for (const item of list) {
    const pts = getAwardedPoint(item, INCLUDE_BONUS);

    // 최대 상품 갱신
    if (pts > maxPts) {
      maxPts = pts;
      maxItem = item;
    }

    // 요구치 이상 중 최소 갱신
    if (pts >= shortageAmount && pts < candidatePts) {
      candidate = item;
      candidatePts = pts;
    }
  }

  return candidate ?? maxItem; // 없으면 최대 상품 반환
};

const pickDefaultProduct = (shortageAmountString) => {
  const shortageAmount = toPointInt(shortageAmountString);
  const requireItem = findRequireRadio(shortageAmount);

  if (requireItem && requireItem.productId) {
    selectedRadio.value = requireItem.productId;
    updateGoods();
  }
};

// --- RN WebView 메시지 수신 ---
const { accessToken: rnAccessToken, iapProducts: globalIapProducts } = useRnMessage({
  onCustom: async (data) => {
    const { action, payload } = data;

    if (action === 'iapResult' && payload?.iapResult) {
      isPurchasing.value = false;
      if (payload.iapResult.success) {
        await getPoint();
        showAlert({ title: '충전이 완료되었습니다.' });
      }
    }

    if (action === 'iapError' && payload?.iapError) {
      isPurchasing.value = false;
      // 사용자 취소는 조용히 처리
      if (payload.iapError.code === 'E_USER_CANCELLED') return;
      showAlert({ title: payload.iapError.message || '결제 중 오류가 발생했습니다.' });
    }
  },
});

// globalIapProducts 변화 감지 → 자동 세팅 (init/refresh 양쪽 모두 처리)
watch(globalIapProducts, (v) => {
  if (v && window.__IAP_SUPPORTED__) setupIapMode(v);
});

/** 앱에 IAP 재조회 요청 */
const requestIapRetry = () => {
  sendMessageToApp({ action: 'retryIap' });
};

onMounted(async () => {
  // SMARTWEL 여부를 먼저 확정해야 기본 선택 깜빡임이 없음
  if (sessionStorage.getItem('X-Token')) await myInfoStore.fetchMyInfo();

  const isIapSupported = !!window.__IAP_SUPPORTED__;

  if (isIapSupported) {
    // 앱 환경: IAP 상품만 사용 (웹 상품 호출 금지)
    if (globalIapProducts.value) {
      // 이미 수신된 경우 즉시 세팅
      setupIapMode(globalIapProducts.value);
    } else {
      // 아직 수신 안 됨 → 재조회 요청, 3초 후 한 번 더 재시도
      requestIapRetry();
      setTimeout(() => {
        if (!globalIapProducts.value) requestIapRetry();
      }, 3000);
    }
  } else {
    // 웹 환경: 웹 상품 fetch
    await goodsList();
  }

  if (sessionStorage.getItem('X-Token')) getPoint();
  getTermsAndPolicies(2728);
  pickDefaultProduct(route.query.shortageAmount);
});

// SMARTWEL 회원 상태가 뒤늦게 확정되는 케이스 대응
watch(isSmartwelMember, (v) => {
  if (!v) return;
  if (isMobile.value && selectedRadio.value) {
    selectedRadio.value = '';
    updateGoods();
  }
});

/** IAP 모드: 선택된 상품의 스토어 가격 표시 */
const iapLocalizedPrice = computed(() => {
  if (!isIapMode.value || !radioContent.value) return '';
  const item = radioContent.value.find((p) => p.productId === selectedRadio.value);
  return item?.localizedPrice || '';
});

const updateGoods = async () => {
  //첫번째값으로 선택
  //selectedRadio.value = radioContent.value[0].productId
  //const selectedItem = filteredProducts.value.find((item) => item.productId === selectedRadio.value)
  const selectedItem = radioContent.value.find((item) => item.productId === selectedRadio.value);

  if (selectedItem) {
    const { basePoint, mainBonusPoint, mainBonusLabel, pcBonusPoint, totalBonusPoint } =
      extractPointSummary(selectedItem);

    // 앱(WebView)에서는 PC 충전 보너스를 합산/표시에서 제외한다.
    const awardedBonusPoint = isWebView.value ? mainBonusPoint : totalBonusPoint;

    goodsName.value =
      awardedBonusPoint > 0
        ? `${formatNumber(basePoint)} P (+${formatNumber(awardedBonusPoint)} P 보너스)`
        : `${formatNumber(basePoint)} P`;
    goodsOrderName.value = `${selectedItem.name}`;
    goodsAmt.value = selectedItem.price;
    currentBonusPoint.value = mainBonusPoint;
    currentJustPoint.value = basePoint;
    currentPointLabel.value = mainBonusLabel;
    currentPcBonusPoint.value = isWebView.value ? 0 : pcBonusPoint;
  } else {
    goodsName.value = '';
    goodsOrderName.value = '';
    goodsAmt.value = 0;
    currentBonusPoint.value = 0;
    currentJustPoint.value = 0;
    currentPointLabel.value = '';
    currentPcBonusPoint.value = 0;
  }

  //이용약관 동의 false
  agree.value = false;
};

const termsAndPolicies = ref(null);
const showPopup = ref(false);
const popupButtonText = ref('동의하기');
/** 약관 및 정책 데이터 요청 */
const getTermsAndPolicies = async (pageId) => {
  try {
    const result = await getTermsAndPoliciesAPIOne(pageId);
    termsAndPolicies.value = (result.contents ?? '').replace(
      /(<div>\s*<br\s*\/?>\s*<\/div>){3,}/gi,
      '<div><br></div>',
    );
  } catch (e) {
    console.log('useJoin getTermsAndPolicies error:', e);
  }
};
const openAgree = (val) => {
  showPopup.value = val;
};

useBackLayer({
  id: 'purchase-points-terms',
  isOpen: showPopup,
  closeFromBack: () => openAgree(false),
});
useBackLayer({
  id: 'purchase-points-toss-payment',
  isOpen: computed(() => !!popTossPayment.value),
  closeFromBack: close,
});
</script>

<template>
  <div class="my-info-wrap purchase-points" :class="{ 'is-app': isWebView }">
    <div class="my-tit-box com-bg-my-box h-small guide com-pc-title-box">
      <div class="tit">낭만 포인트 충전</div>
      <div v-if="isSmartwelMember" class="pc-smartwel-actions">
        <button type="button" class="pc-smartwel-actions__btn" @click="goExchange">
          <span>복지포인트 사용하러 가기</span>
        </button>
      </div>
    </div>

    <div
      class="com-app-tit-box purchase-service-header"
      :class="{ 'purchase-service-header--app': isWebView }"
    >
      <div class="tit">서비스 구매</div>
      <div class="btn-right">
        <button v-if="isWebView" class="btn-back" @click="handleClose">
          <IconHeaderClose />
        </button>
        <button v-else class="btn-mypage" @click="toggleMypageMenu">
          <i class="icon-mypage"></i>
        </button>
      </div>
    </div>

    <div class="com-app-tab-box purchase-service-tabs">
      <button class="tab-button" @click="openTicketTab">분석권 구매</button>
      <button class="tab-button selected">포인트 충전</button>
      <button class="tab-button" @click="openGiftTab">선물함</button>
    </div>

    <!-- 모바일 보유 포인트 바 -->
    <div class="mobile-point-status">
      <span class="mobile-point-status__label">보유 포인트</span>
      <span class="mobile-point-status__value">
        <i class="mobile-point-status__icon"></i>
        {{ formatNumber(currentPoints) }} P
      </span>
    </div>

    <!-- 복지찬스 영역 (모바일, 복지찬스 회원만) — 통일된 SmartwelEntryRow 사용
         이 페이지에서는 구독 의미가 아니라 '복지포인트 전환'으로 이동하는 액션 -->
    <div v-if="isSmartwelMember" class="smartwell-section mobile-only">
      <SmartwelEntryRow
        variant="dropdown-card"
        :point="smartwellBalanceKrw"
        cta-label="복지포인트 사용"
        @click-sub="goExchange"
        @click-point="goExchange"
      />
    </div>

    <div class="points-scroll-content">
      <div class="myinfo-box com-bg-my-box guide">
        <CustomRadiosPoint
          class="radio-custom-01 st-point"
          v-model:selected-radio="selectedRadio"
          :radio-content="radioContent"
          :grid-column="pointGridColumn"
          @update:selectedRadio="updateGoods"
        />

        <!-- PC에서만 보이는 구분선 -->
        <div class="gap-xxl3"></div>
        <div class="my-line-01"></div>
        <div class="gap-xxl3"></div>
        <!-- PC에서는 myinfo-box 안에 표시 -->
        <div class="my-pt-btm pc-only">
          <dl>
            <dt><span class="txt-block">• 충전 포인트</span></dt>
            <dd class="txt1">
              {{ formatNumber(currentJustPoint) }} P
              <span class="txt1-1" v-if="currentBonusPoint > 0"
                >+ {{ formatNumber(currentBonusPoint) }} P
                <span class="txt1-1-pop" v-if="currentBonusPoint > 0 && currentPointLabel">{{
                  currentPointLabel
                }}</span>
              </span>
              <span class="txt1-2" v-if="currentPcBonusPoint > 0"
                >+ {{ formatNumber(currentPcBonusPoint) }} P</span
              >
            </dd>
          </dl>
          <dl>
            <dt>
              <span class="txt-block">• 결제 금액</span>
            </dt>
            <dd class="txt2">
              {{ isIapMode ? iapLocalizedPrice : `${formatNumber(goodsAmt)} 원` }}
            </dd>
          </dl>
        </div>
      </div>

      <div class="point-info-box charge guide">
        <div class="tit"><span>포인트 충전 안내</span> <i class="icon-info"></i></div>
        <ul>
          <li class="point-info-highlight">PC 충전 시, 5% 추가 적립</li>
          <li>포인트는 선불 결제 방식으로 충전되며, 원하는 금액만큼 충전 가능합니다.</li>
          <li>
            충전된 포인트는 만세력 서비스 내에서 유효하며, 특정 서비스 및 콘텐츠 결제에 사용
            가능합니다.
          </li>
          <li>
            보너스 포인트는 일정 조건에서 제공되며, 지급받은 포인트는 사용기간, 사용 대상에서 제한이
            있을 수 있습니다.
          </li>
          <li>충전된 포인트는 타인에게 양도하거나 현금으로 전환할 수 없습니다.</li>
        </ul>
      </div>
    </div>

    <!-- 하단 고정 결제 바 (PC: fixed, 모바일: fixed) -->
    <div class="gap-sm-2"></div>
    <div v-if="selectedRadio" class="myinfo-box com-bg-my-box pay-box fixed-pay-box">
      <div class="inner-base">
        <!-- 모바일 footer 고정 요약 -->
        <div class="my-pt-btm mobile-only mobile-footer-summary">
          <dl>
            <dt><span class="txt-block">충전 포인트</span></dt>
            <dd class="txt1">
              {{ formatNumber(currentJustPoint) }} P
              <span class="txt1-1" v-if="currentBonusPoint > 0"
                >+ {{ formatNumber(currentBonusPoint) }} P
                <span class="txt1-1-pop" v-if="currentBonusPoint > 0 && currentPointLabel">{{
                  currentPointLabel
                }}</span>
              </span>
              <span class="txt1-2" v-if="currentPcBonusPoint > 0"
                >+ {{ formatNumber(currentPcBonusPoint) }} P</span
              >
            </dd>
          </dl>
          <dl>
            <dt>
              <span class="txt-block">결제 금액</span>
            </dt>
            <dd class="txt2">
              {{ isIapMode ? iapLocalizedPrice : `${formatNumber(goodsAmt)} 원` }}
            </dd>
          </dl>
        </div>
        <div class="pay-box__spacer"></div>
        <div class="pay-box__content">
          <div class="product-name-box">
            <span>상품명</span><span class="amount">{{ goodsOrderName }}</span>
          </div>
          <div class="right-box">
            <div class="bg-box-01">
              <input type="checkbox" class="checkbox st-point" v-model="agree" />
              결제 내용에 동의합니다.
              <span class="txt-tos" @click="openAgree(true)">이용약관</span>
            </div>
            <div class="com-btn-box">
              <VBtn
                type="submit"
                style="width: 280px"
                @click="openPayment"
                :disabled="agree === false || isPurchasing"
              >
                <template v-if="isPurchasing">결제 진행중...</template>
                <template v-else-if="isIapMode">{{ iapLocalizedPrice }} 결제하기</template>
                <template v-else>{{ formatNumber(goodsAmt) }}원 결제하기</template>
              </VBtn>
            </div>
          </div>
        </div>

        <VDialog
          v-if="!isIapMode"
          v-model="popTossPayment"
          :class="isMobile ? 'toss-dialog-mobile' : 'v-dialog-sm dialog-st1'"
          :max-width="isMobile ? undefined : '800px'"
          :fullscreen="isMobile"
          :transition="isMobile ? 'dialog-bottom-transition' : undefined"
          @click:outside="close"
        >
          <div class="toss-payment-wrap">
            <!-- 모바일 헤더 -->
            <div v-if="isMobile" class="toss-payment-header">
              <span class="toss-payment-header__title">결제하기</span>
              <button class="toss-payment-header__close" @click="close">
                <IconHeaderClose />
              </button>
            </div>

            <div class="toss-payment-body">
              <!-- 결제 UI -->
              <div id="payment-method"></div>

              <!-- 이용약관 UI -->
              <div id="agreement"></div>
            </div>

            <!-- 결제하기 버튼 -->
            <div class="guide__popup-btn">
              <VBtn
                type="submit"
                :style="isMobile ? 'width: 100%' : 'width: 80%; margin: 20px auto 40px auto'"
                @click="doPayment"
              >
                결제하기
              </VBtn>
            </div>
          </div>
        </VDialog>
      </div>
    </div>

    <!-- 마이페이지 드롭다운 메뉴 (모바일 웹 전용) -->
    <Teleport to="body">
      <div
        v-if="isMypageMenuOpen && !isWebView"
        class="mypage-menu-overlay"
        @click="closeMypageMenu"
      >
        <div class="mypage-menu-dropdown" @click.stop>
          <div class="login-msg-box">
            <button class="btn-home" :class="{ active: isHomeRoute }" @click="handleGoHome">
              <i class="icon-home"></i>
              홈
            </button>
            <template v-if="isLoggedIn">
              <div class="txt-box">
                <span
                  ><span class="txt1" style="margin-right: 2px">{{ displayName || '게스트' }}</span
                  >님</span
                >
                <span class="txt2">행복한 하루 되세요</span>
              </div>
            </template>
            <template v-else>
              <div class="txt-box">
                <span class="txt1">게스트</span>
                <span class="txt2">로그인 후 이용해주세요</span>
              </div>
            </template>

            <DropdownCreditInfo
              v-if="isLoggedIn"
              ref="creditInfoRef"
              :is-logged-in="isLoggedIn"
              :visible="isMypageMenuOpen"
              @close-menu="closeMypageMenu"
            />

            <div class="gnb-menu-list">
              <template v-for="(menu, key) in gnbMenuLinks" :key="key">
                <router-link
                  v-if="menu.name !== '낭만만세력은?'"
                  :to="menu.link"
                  :class="{ active: isMenuActive(menu) }"
                  class="gnb-menu-item"
                  @click="handleMenuClick(menu)"
                >
                  {{ menu.name }}
                </router-link>
                <a
                  v-else
                  :href="$router.resolve(menu.link).href"
                  target="_blank"
                  rel="noopener"
                  class="gnb-menu-item"
                  @click="handleMenuClick(menu)"
                >
                  {{ menu.name }}
                </a>
              </template>
            </div>

            <template v-if="isLoggedIn">
              <MypageLnbTabMenu />
              <button class="btn-xs line" @click="handleLogout">로그아웃</button>
            </template>
            <template v-else>
              <button class="btn-xs line login-btn" @click="handleLogin">로그인</button>
            </template>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
  <div v-if="showPopup" class="popup-overlay popup-overlay--terms" @click="openAgree(false)">
    <div class="popup-box" @click.stop>
      <div class="popup-header">
        서비스 이용약관
        <button class="dial-close" @click="openAgree(false)">닫기버튼</button>
      </div>

      <div class="popup-message" v-html="termsAndPolicies"></div>
      <button class="popup-button" @click="openAgree(false)">확인</button>
    </div>
  </div>

  <AlertComponent ref="alertRef" :title="dialogTitle" :text="dialogText" />
  <ConfirmComponent
    ref="confirmRef"
    :title="dialogTitle"
    :title2="dialogTitle2"
    :text="dialogText"
    :warning="dialogWarning"
    :nextUrl="dialogNextUrl"
  />
</template>

<style lang="scss">
@import '@/assets/webview/guide_product.scss';

.purchase-layout:has(.purchase-points) .nc-footer {
  display: none !important;
}

.purchase-points {
  padding-bottom: 80px;

  .com-bg-my-box {
    &.fixed-pay-box {
      z-index: 10;
      position: fixed;
      left: 0;
      right: 0;
      bottom: 0;

      margin-bottom: 0;
      padding: 28px 0 calc(28px + var(--safe-area-bottom, 0px)) 40px !important;
      width: 100%;

      justify-content: flex-end;
      align-items: center;

      background: var(--gray-100-white, #fff);
      box-shadow: 0px -10px 20px 0px rgba(0, 0, 0, 0.1);
      border-radius: 0;
      .inner-base {
        width: 100%;
        max-width: 1360px;

        display: grid;
        grid-template-columns: 260px 1fr;
        justify-content: center;
        align-items: center;
        .pay-box__spacer {
          display: flex;
        }
        .pay-box__content {
          display: flex;
          flex-direction: row;
          .right-box {
            width: 100%;
            display: flex;
            justify-content: flex-end;
            flex-wrap: wrap;
          }
          .product-name-box {
            display: flex;
            flex-direction: column;
            flex-shrink: 0;
            // width: 200px;
            color: var(--Gray-700, #575757);
            font-size: 14px;
            font-style: normal;
            font-weight: 500;
            line-height: 18px; /* 128.571% */
            .amount {
              color: var(--orange-300-main, #eb4c10);
              font-size: 20px;
              font-style: normal;
              font-weight: 700;
              line-height: 24px; /* 120% */
            }
          }
          .bg-box-01 {
            background: transparent;
            justify-content: flex-start;
            width: auto;
            color: var(--Gray-800, #333);
            font-size: 14px;
            font-weight: 700;
            padding-right: 0;
            .checkbox {
              width: 20px;
              height: 20px;
              padding-left: 20px;
            }
            .txt-tos {
              color: var(--Gray-600, #757575);
              text-align: right;
              font-family: Pretendard;
              font-size: 14px;
              font-style: normal;
              font-weight: 500;
              line-height: 18px; /* 128.571% */
              text-decoration-line: underline;
              text-decoration-style: solid;
              text-decoration-skip-ink: none;
              text-decoration-thickness: auto;
              text-underline-offset: auto;
              text-underline-position: from-font;
            }
          }
          .com-btn-box {
            margin-left: 40px;
            .v-btn--disabled.v-btn--variant-elevated,
            .v-btn--disabled.v-btn--variant-flat {
              background-color: #333 !important;
            }
            .v-btn__content {
              color: var(--gray-100-white, #fff);
              font-size: 15px;
              font-weight: 700;
            }
          }
        }
      }
    }
  }

  .radio-custom-01 {
    padding: 0 0 0 12px;
  }
}

.purchase-points .radio-custom-01.st-point .v-label.custom-input .st-point-per {
  position: absolute;
  left: auto;
  right: 20px;
  bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  white-space: nowrap;
  border-radius: 0;
  background: transparent;
  padding: 0;
  color: inherit;
}

.purchase-points .radio-custom-01.st-point .v-label.custom-input .st-point-per .st-repo-btm1 {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  gap: 24px;
  width: auto;
}

.purchase-points .radio-custom-01.st-point .v-label.custom-input .st-point-per .discount-percent {
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
  color: #ff6b6b;
  padding: 2px 4px;
  border-radius: 4px;
  background: rgba(255, 107, 107, 0.1);
}

.purchase-points .radio-custom-01.st-point .v-label.custom-input .st-point-per .extra-percent {
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
  color: #4e80d6;
  padding: 2px 4px;
  border-radius: 4px;
  background: rgba(78, 128, 214, 0.1);
}

.purchase-points .my-pt-btm .txt1 .txt1-2 {
  color: #4e80d6;
}

/* ===== PC 복지찬스 회원 영역 ===== */
.pc-smartwel-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}
.pc-smartwel-actions__badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 11px;
  border-radius: 100px;
  background: #faf7f1;
  border: 1px solid #ece8e0;
  color: #6d4209;
  font-size: 12.5px;
  font-weight: 700;
  letter-spacing: -0.2px;
  line-height: 18px;
}
.pc-smartwel-actions__badge::before {
  content: '';
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #d85a30;
  display: inline-block;
}
/* 복지P 사용하러 가기 버튼 — exchange.vue 타이틀 우측 버튼과 동일 스타일
   (흰 배경 · 회색 테두리, hover 시 orange300) */
.pc-smartwel-actions__btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 9px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #fff;
  color: #374151;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: -0.3px;
  cursor: pointer;
  transition: all 0.15s;
}
.pc-smartwel-actions__btn:hover:not(:disabled) {
  background: #fff;
  border-color: var(--orange-300-main, #eb4c10);
  color: var(--orange-300-main, #eb4c10);
}
.pc-smartwel-actions__btn:disabled {
  background: #fff;
  border-color: #e5e7eb;
  color: #d1d5db;
  cursor: not-allowed;
}
.pc-smartwel-actions__btn-icon {
  font-size: 13px;
}

// 보유 포인트 바 (기본 숨김, 모바일만 표시)
.mobile-point-status {
  display: none;
}

// 복지찬스 영역 (기본 숨김, 모바일만 표시)
.smartwell-section.mobile-only {
  display: none;
}

// PC/모바일 전용 요소
.my-pt-btm.mobile-only {
  display: none;
}

@media (max-width: 768px) {
  .purchase-service-header {
    justify-content: center;
    border-bottom: 1px solid #ebebeb;

    .btn-right {
      position: absolute;
      right: 8px;
      display: flex;
      align-items: center;
      gap: 0;
    }

    .btn-right .btn-back {
      display: inline-flex;
      width: 24px;
      height: 24px;
      align-items: center;
      justify-content: center;
      border: 0;
      background: transparent;
      padding: 0;
      cursor: pointer;
    }

    .btn-right .btn-mypage {
      display: inline-flex;
      width: 36px;
      height: 36px;
      align-items: center;
      justify-content: center;
      border: none;
      background: transparent;
      padding: 0;
      cursor: pointer;
    }

    .btn-right .btn-mypage .icon-mypage {
      display: flex;
      width: 32px;
      height: 32px;
      border-radius: 100%;
      background: url('@manseImages/icon-mypage.png') center center no-repeat;
      background-size: 32px auto;
      font-size: 0;
    }

    &.purchase-service-header--app {
      .btn-right {
        right: 16px;
      }
    }
  }

  .purchase-service-tabs {
    height: 44px;
    padding: 0 15px;
    border-bottom: 1px solid #ebebeb;
    justify-content: space-between;

    .tab-button {
      flex: 1;
      max-width: none;
      padding: 6px 0;
      font-size: 16px;
      font-weight: 500;
      line-height: 30px;
    }
  }

  // 전체 컨테이너: 부모 스크롤 방식
  .purchase-points {
    position: relative;
    width: 100vw;
    min-height: 100dvh;
    display: flex;
    flex-direction: column;
    margin-bottom: 0;
    padding-bottom: 0;

    // 콘텐츠 영역: 부모 스크롤에 맡김
    .points-scroll-content {
      background: #f8f8f8;
      padding-bottom: calc(230px + var(--safe-area-bottom, 0));
    }
  }

  // 복지찬스 영역 (SmartwelEntryRow 래퍼) — 헤더/탭/포인트바 아래 sticky
  .smartwell-section.mobile-only {
    display: block !important;
    padding: 8px 16px;
    background: #fff;
    position: sticky;
    top: calc(146px + var(--safe-area-top, 0)); // 헤더(52px) + 탭(44px) + 보유 포인트 바(50px)
    z-index: 9;
  }

  // 보유 포인트 바
  .mobile-point-status {
    display: flex !important;
    justify-content: space-between;
    align-items: center;
    padding: 14px 16px;
    background: #f8f8f8;
    position: sticky;
    top: calc(96px + var(--safe-area-top, 0)); // 헤더(52px) + 탭(44px)
    z-index: 10;

    &__label {
      color: #333;
      font-size: 14px;
      font-weight: 700;
      line-height: 22px;
    }

    &__value {
      display: flex;
      align-items: center;
      gap: 8px;
      color: #eb4c10;
      font-size: 16px;
      font-weight: 700;
      line-height: 22px;
    }

    &__icon {
      display: inline-block;
      width: 20px;
      height: 20px;
      background: url('/src/assets/images_manse/point.svg') center center no-repeat;
      background-size: contain;
    }
  }

  // ===== 상품 리스트 박스 =====
  .purchase-points .myinfo-box.com-bg-my-box.guide {
    display: block !important;
    padding: 8px 16px 28px !important;
    margin: 0 !important;
    background: #fff !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    height: auto !important;
    max-height: none !important;
    overflow: visible !important;
  }

  // CustomRadiosPoint 래퍼
  .purchase-points .radio-custom-01.st-point {
    display: block !important;
    padding: 0 !important;
  }

  // VRadioGroup 리셋
  .purchase-points .radio-custom-01.st-point .v-input {
    display: block !important;
  }
  .purchase-points .radio-custom-01.st-point .v-input__control {
    display: block !important;
    width: 100% !important;
  }
  .purchase-points .radio-custom-01.st-point .v-selection-control-group {
    display: block !important;
  }

  // VRow → 세로 쌓기
  .purchase-points .radio-custom-01.st-point .row-line {
    display: flex !important;
    flex-direction: column !important;
    flex-wrap: nowrap !important;
    margin: 0 !important;
    gap: 0 !important;
  }

  // VCol → 100% 너비
  .purchase-points .row-line .v-col,
  .purchase-points .row-line .v-col-12,
  .purchase-points .row-line .v-col-5,
  .purchase-points .row-line .v-col-sm-3,
  .purchase-points .row-line .p-col {
    max-width: 100% !important;
    flex: 0 0 auto !important;
    width: 100% !important;
    padding: 4px 0 !important;
  }

  // ===== 상품 카드 (각 항목) =====
  .purchase-points .custom-radio.st-point-label,
  .purchase-points .st-point-label {
    display: flex !important;
    flex-direction: row !important;
    flex-wrap: nowrap !important;
    align-items: center !important;
    width: 100% !important;
    min-height: auto !important;
    height: auto !important;
    padding: 16px 20px !important;

    // 라디오 버튼 wrapper → 오른쪽 끝에 배치
    > div:first-child {
      order: 2 !important;
      flex-shrink: 0 !important;
      margin-left: 8px !important;

      .radio-btn.point {
        position: relative !important;
        right: auto !important;
        display: flex !important;
        align-items: center !important;
      }
    }

    // 콘텐츠 영역 → 왼쪽, flex:1
    .flex-grow-1 {
      order: 1 !important;
      flex: 1 !important;
      min-width: 0 !important;

      .d-flex {
        display: flex !important;
        flex-direction: row !important;
        flex-wrap: wrap !important;
        align-items: center !important;
        gap: 8px !important;
        row-gap: 6px !important;
        margin-bottom: 0 !important;

        .cr-title {
          font-size: 20px !important;
          line-height: 24px !important;
          white-space: nowrap !important;
          flex-shrink: 0 !important;
        }

        .v-spacer {
          display: none !important;
        }
      }
    }

    // 선택된 상품 카드: 텍스트 주황색 (PC와 동일)
    &.active .cr-title {
      color: var(--orange-300-main, #eb4c10) !important;
    }

    // 보너스 뱃지: PC의 absolute 포지션 리셋 → 인라인 배치
    .st-point-per {
      position: static !important;
      left: auto !important;
      right: auto !important;
      top: auto !important;
      bottom: auto !important;
      display: flex !important;
      align-items: center !important;
      justify-content: flex-start !important;
      flex: 1 1 100% !important;
      min-width: 0 !important;
      width: 100% !important;
      max-width: 100% !important;
      gap: 6px !important;
      white-space: normal !important;
      border-radius: 0 !important;
      background: transparent !important;
      padding: 0 !important;
    }

    .st-point-per .st-repo-btm1 {
      display: flex !important;
      flex-direction: row !important;
      flex-wrap: nowrap !important;
      align-items: center !important;
      justify-content: flex-start !important;
      gap: 6px !important;
      min-width: auto !important;
      width: auto !important;
    }

    .st-point-per .discount-percent {
      display: inline-flex !important;
      align-items: center !important;
      font-size: 12px !important;
      font-weight: 700 !important;
      line-height: 16px !important;
      color: #ff6b6b !important;
      padding: 2px 4px !important;
      border-radius: 4px !important;
      background: rgba(255, 107, 107, 0.1) !important;
      white-space: nowrap !important;
    }

    .st-point-per .extra-percent {
      display: inline-flex !important;
      align-items: center !important;
      font-size: 12px !important;
      font-weight: 700 !important;
      line-height: 16px !important;
      color: #4e80d6 !important;
      padding: 2px 4px !important;
      border-radius: 4px !important;
      background: rgba(78, 128, 214, 0.1) !important;
      white-space: nowrap !important;
    }
  }

  // 앱(WebView)에서는 보너스 뱃지를 제목과 같은 줄에 표시
  .purchase-points.is-app .custom-radio.st-point-label,
  .purchase-points.is-app .st-point-label {
    .flex-grow-1 .d-flex {
      flex-wrap: nowrap !important;
    }

    .st-point-per {
      flex: 0 1 auto !important;
      width: auto !important;
      max-width: none !important;
      min-width: 0 !important;
      overflow: hidden !important;
    }
  }

  // ===== PC 전용 숨김 =====
  .purchase-points .gap-xxl3,
  .purchase-points .my-line-01,
  .purchase-points .gap-sm-2,
  .purchase-points .my-pt-btm.pc-only {
    display: none !important;
  }

  // ===== 모바일 전용 충전포인트/결제금액 =====
  .my-pt-btm.mobile-only.mobile-footer-summary {
    display: block !important;
    height: auto !important;
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
    padding-bottom: 20px !important;
    margin-bottom: 20px !important;
    border-bottom: 1px solid #ebebeb !important;

    &::after {
      display: none !important;
    }

    dl {
      display: flex !important;
      flex-direction: row !important;
      justify-content: space-between !important;
      align-items: center !important;
      flex: none !important;
      margin: 0 !important;
      padding: 0 !important;
      width: 100% !important;
      height: auto !important;
      border: 0 !important;

      & + dl {
        margin-top: 12px !important;
      }

      // PC 글로벌 스타일의 :first-child dd padding-right, :nth-child(2) dt padding-left 리셋
      &:first-child dd,
      &:nth-child(2) dd {
        padding-right: 0 !important;
      }
      &:first-child dt,
      &:nth-child(2) dt {
        padding-left: 0 !important;
      }

      dt {
        flex-shrink: 0;

        .txt-block {
          color: #575757;
          font-size: 15px;
          font-weight: 400;
          line-height: 24px;
          &::before {
            content: none !important;
          }
        }
      }

      dd {
        text-align: right;
        margin: 0 !important;
        flex: 1 !important;

        &.txt1 {
          position: relative !important;
          color: #333;
          font-size: 20px;
          font-weight: 700;
          line-height: 24px;

          .txt1-1 {
            position: relative;
            color: #ff6b6b;
            font-size: inherit;
            font-weight: 700;
            line-height: inherit;
          }

          .txt1-2 {
            color: #4e80d6;
            font-size: inherit;
            font-weight: 700;
            line-height: inherit;
          }

          .txt1-1-pop {
            position: absolute !important;
            left: auto !important;
            right: 0 !important;
            top: -40px !important;
            bottom: auto !important;
            transform: translateZ(0) !important;
            will-change: transform;
            -webkit-backface-visibility: hidden;
            backface-visibility: hidden;
            margin-left: 0 !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            min-height: 28px !important;
            height: 28px !important;
            padding: 5px 10px !important;
            font-size: 13px !important;
            font-weight: 700 !important;
            line-height: 18px !important;
            text-align: center;
            max-width: calc(100vw - 40px) !important;
            white-space: nowrap !important;
            word-break: normal !important;
            overflow-wrap: normal !important;
            overflow: visible !important;
            border-radius: 30px;
            background: var(--Orange-400, #ff6b6b);
            color: #fff;
            z-index: 1;

            &::before {
              display: block !important;
              position: absolute !important;
              bottom: -11px !important;
              left: auto !important;
              right: 16px !important;
              margin-left: 0 !important;
              content: '' !important;
              width: 0 !important;
              height: 0 !important;
              background: none !important;
              transform: none !important;
              border-left: 8px solid transparent !important;
              border-right: 8px solid transparent !important;
              border-top: 12px solid var(--Orange-400, #ff6b6b) !important;
              border-radius: 0 !important;
              z-index: 1 !important;
            }
          }
        }

        &.txt2 {
          color: #eb4c10;
          font-size: 20px;
          font-weight: 700;
          line-height: 24px;
        }
      }
    }
  }

  // ===== 포인트 충전 안내 =====
  .purchase-points .point-info-box.charge.guide {
    display: block !important;
    margin: 0 !important;
    padding: 28px 16px !important;
    background: #f8f8f8 !important;
    border-radius: 0 !important;
    box-shadow: none !important;

    .tit {
      display: flex !important;
      flex-direction: row !important;
      align-items: center !important;
      flex-wrap: nowrap !important;
      flex: none !important;
      margin-right: 0 !important;
      font-size: 14px;
      font-weight: 700;
      color: #575757;
      line-height: 20px;
      margin-bottom: 12px;

      .icon-info {
        flex-shrink: 0;
      }
    }

    ul {
      display: block !important;
      margin: 0 !important;
      padding: 0 !important;
      list-style: none !important;

      li {
        display: block !important;
        position: relative;
        padding-left: 12px;
        margin-bottom: 0;
        color: #575757;
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;

        & + li {
          margin-top: 8px;
        }

        &::before {
          content: '' !important;
          display: block !important;
          position: absolute;
          left: 0;
          top: 8px;
          width: 3px !important;
          height: 3px !important;
          background: #575757 !important;
          border-radius: 50% !important;
        }

        &.point-info-highlight {
          color: var(--orange-300-main, #eb4c10) !important;
          font-size: 13px;
          font-weight: 700;
          line-height: 18px;

          &::before {
            background: var(--orange-300-main, #eb4c10) !important;
          }
        }
      }
    }
  }

  // ===== 하단 고정 결제 바 =====
  .purchase-points .com-bg-my-box.fixed-pay-box {
    position: fixed !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    z-index: 100 !important;
    padding: 20px 16px calc(20px + var(--safe-area-bottom, 0px)) !important;
    margin: 0 !important;
    border-radius: 0 !important;
    box-shadow: 0px -10px 20px 0px rgba(0, 0, 0, 0.2);
    background: #fff !important;

    .inner-base {
      display: flex !important;
      flex-direction: column !important;
      gap: 0 !important;
      max-width: 100% !important;
      padding: 0 !important;

      .pay-box__spacer {
        display: none !important;
      }

      .pay-box__content {
        display: flex !important;
        flex-direction: column !important;
        gap: 20px !important;
        width: 100% !important;

        .product-name-box {
          display: none !important;
        }

        .right-box {
          display: flex !important;
          flex-direction: column !important;
          width: 100% !important;
          gap: 16px !important;

          .bg-box-01 {
            display: flex !important;
            flex-direction: row !important;
            justify-content: flex-start !important;
            align-items: center !important;
            gap: 6px !important;
            margin: 0 !important;
            padding: 0 !important;
            width: 100% !important;
            font-size: 15px;
            font-weight: 500;
            color: #333;
            background: transparent !important;

            .checkbox {
              width: 20px;
              height: 20px;
              flex-shrink: 0;
            }

            .txt-tos {
              font-size: 15px;
              line-height: 18px;
              color: #757575;
              border-bottom: 1px solid #757575;
              text-decoration: none;
              margin-left: auto;
            }
          }

          .com-btn-box {
            margin: 0 !important;
            padding: 0 !important;

            button,
            .v-btn {
              width: 100% !important;
              height: 48px !important;
              min-height: 48px !important;
              border-radius: 8px !important;
            }

            .v-btn--disabled.v-btn--variant-elevated,
            .v-btn--disabled.v-btn--variant-flat {
              background-color: #949494 !important;
            }
          }
        }
      }
    }
  }

  // ===== 결제 팝업 (풀스크린) =====
  .toss-dialog-mobile {
    .v-overlay__content {
      max-width: 100% !important;
      width: 100% !important;
      height: 100% !important;
      max-height: 100% !important;
      margin: 0 !important;
      padding: 0 !important;
      border-radius: 0 !important;
      inset: 0 !important;
    }
  }

  // 마이페이지 드롭다운
  .mypage-menu-overlay {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: transparent;
    z-index: 998;
  }

  .mypage-menu-dropdown {
    display: block;
    position: fixed;
    top: 60px;
    right: 16px;
    width: 280px;
    max-height: calc(100vh - 76px);
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 999;
    overflow-y: auto;
    overflow-x: hidden;
    overscroll-behavior: contain;

    .login-msg-box {
      position: relative;
      padding: 20px;
      width: 100%;
      box-sizing: border-box;

      .btn-home {
        position: absolute;
        top: 14px;
        right: 14px;
        display: inline-flex;
        align-items: center;
        gap: 4px;
        height: 28px;
        padding: 0 8px;
        border: 1px solid #e6e6e6;
        border-radius: 14px;
        background: #fff;
        color: #333;
        font-size: 12px;
        font-weight: 600;
        cursor: pointer;

        .icon-home {
          width: 14px;
          height: 14px;
          background-color: currentColor;
          -webkit-mask: url('@manseImages/icon_home.svg') center center / contain no-repeat;
          mask: url('@manseImages/icon_home.svg') center center / contain no-repeat;
        }

        &.active {
          color: #eb4c10;
          border-color: #f7d1c3;
          background: #fff5f2;
        }
      }

      .txt-box {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        color: #333;
        font-size: 15px;
        margin-bottom: 16px;
        padding-top: 6px;

        .txt1 {
          font-weight: 700;
        }

        .txt2 {
          color: #757575;
          font-size: 13px;
          margin-top: 4px;
        }
      }

      .gnb-menu-list {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin-bottom: 16px;
        padding-top: 12px;
        border-top: 1px solid #f5f5f5;

        .gnb-menu-item {
          display: inline-block;
          padding: 6px 12px;
          color: #333;
          font-family: Pretendard;
          font-size: 13px;
          font-weight: 500;
          line-height: 20px;
          text-decoration: none;
          border-radius: 6px;
          background: #f9f9f9;
          transition: all 0.2s;
          white-space: nowrap;

          &:hover {
            background: #f0f0f0;
            color: #eb4c10;
          }

          &.active {
            background: #fff5f2;
            color: #eb4c10;
            font-weight: 600;
          }
        }
      }

      .btn-xs.line {
        width: 100%;
        margin-top: 8px;
        padding: 8px 16px;
        border: 1px solid #ebebeb;
        border-radius: 6px;
        background: #fff;
        color: #333;
        font-size: 13px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s;

        &:hover {
          background: #f5f5f5;
        }

        &.login-btn {
          background: var(--orange-300-main, #eb4c10);
          color: #fff;
          border-color: var(--orange-300-main, #eb4c10);

          &:hover {
            background: #d43d0e;
            border-color: #d43d0e;
          }
        }
      }
    }
  }
}
</style>

<style scoped lang="scss">
.point-info-box.guide {
  background: var(--Gray-200, #f8f8f8);
  padding: 40px;

  li.point-info-highlight {
    color: var(--orange-300-main, #eb4c10);
    font-size: 13px;
    font-weight: 700;
    line-height: 18px;

    &::before {
      background-color: var(--orange-300-main, #eb4c10) !important;
    }
  }
}

.guide__popup-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: white;
}

// 결제 팝업 래퍼
.toss-payment-wrap {
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
}

@media (max-width: 768px) {
  .toss-payment-wrap {
    height: 100%;
    border-radius: 0;
  }

  .toss-payment-header {
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    height: 52px;
    flex-shrink: 0;
    border-bottom: 1px solid #f0f0f0;

    &__title {
      font-size: 17px;
      font-weight: 700;
      color: #333;
    }

    &__close {
      position: absolute;
      right: 16px;
      top: 50%;
      transform: translateY(-50%);
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      border: none;
      background: transparent;
      cursor: pointer;
      padding: 0;
    }
  }

  .toss-payment-body {
    flex: 1;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  .guide__popup-btn {
    flex-shrink: 0;
    padding: 16px;
    background: #fff;
    box-shadow: 0px -4px 12px rgba(0, 0, 0, 0.08);
  }
}

</style>
